/**
 * Utility functions for Azure Data Explorer (ADX) and Kusto operations
 */
import { logEvent } from "../helper/AppInsightsHelper";

// Base URL for Azure Data Explorer (ADX) - this is the cluster endpoint with database reference
// The URL points to the specific cluster (cxedataplatformcluster) in West US 2 region
// and targets the Security360 database with a query parameter placeholder
const ADX_BASE_URL = "https://kusto.azure.com/clusters/cxedataplatformcluster.westus2/databases/Security360?query=";

/**
 * Generates an Azure Data Explorer (ADX) link for a given Kusto query
 * @param query - The Kusto query string
 * @returns The ADX URL with the encoded query, or empty string if query is invalid
 */
export const generateADXLink = async (query: string): Promise<string> => {
  // Check if the query parameter exists and is not just whitespace
  if (!query?.trim()) return "";

  try {
    // Create a new TextEncoder instance to convert string to bytes
    // TextEncoder is a Web API that encodes strings into UTF-8 byte sequences
    const encoder = new TextEncoder();

    // Convert the query string to a Uint8Array of UTF-8 bytes
    // Then create a Blob (binary large object) from those bytes
    // Finally, get a readable stream from the blob for processing
    const inputStream = new Blob([encoder.encode(query)]).stream();

    // Apply GZIP compression to the stream using the Compression Streams API
    // This reduces the size of the query data before encoding to Base64
    const gzipStream = inputStream.pipeThrough(new CompressionStream("gzip"));

    // Convert the compressed stream back to a Blob object
    // Response constructor can read from a stream and convert it to a blob
    const compressedBlob = await new Response(gzipStream).blob();

    // Convert the compressed blob to an ArrayBuffer
    // ArrayBuffer represents a fixed-length binary data buffer
    const compressedArrayBuffer = await compressedBlob.arrayBuffer();

    // Convert ArrayBuffer to Uint8Array for easier manipulation
    // Uint8Array provides a view of the underlying binary data as 8-bit unsigned integers
    const uint8Array = new Uint8Array(compressedArrayBuffer);

    // Convert the compressed binary data to Base64 encoding for URL transmission
    // Step-by-step breakdown of this complex line:
    // 1. Array.from(uint8Array) - Converts Uint8Array to regular JavaScript array
    // 2. ...Array.from(uint8Array) - Spreads array elements as individual arguments
    // 3. String.fromCharCode(...) - Converts each byte value to its corresponding character
    // 4. btoa() - Encodes the resulting string to Base64 format
    // This creates a Base64-encoded string that can be safely transmitted in URLs
    const base64 = btoa(String.fromCharCode(...Array.from(uint8Array)));

    // Create the final ADX URL by concatenating base URL with the Base64-encoded compressed query
    // The base64 string contains the compressed Kusto query in a URL-safe format
    return `${ADX_BASE_URL}${base64}`;
  } catch (error) {
    // Log any errors that occur during compression or encoding
    console.error("Error generating ADX link:", error);
    // Return empty string to indicate failure
    return "";
  }
};

/**
 * Opens a Kusto query or ADX link in Azure Data Explorer in a new tab
 * @param queryOrLink - Either a Kusto query string or a ready-made ADX link
 * @param onError - Optional callback for error handling
 */
export const openQueryInADX = async (queryOrLink: string, onError?: (message: string) => void): Promise<void> => {
  logEvent("Open Query in ADX", "AI Hub", "adx-utils", [{ query: queryOrLink }]);
  // Validate input parameter - check if queryOrLink exists and is not just whitespace
  if (!queryOrLink?.trim()) {
    // Create descriptive error message for empty/invalid input
    const errorMessage = "No query or link provided.";
    // Use provided error callback if available, otherwise fallback to browser alert
    if (onError) {
      onError(errorMessage);
    } else {
      alert(errorMessage);
    }
    // Exit function early since there's nothing to process
    return;
  }

  // Determine if the input is already a complete ADX link or a raw Kusto query
  // Check if it starts with http protocol and contains the kusto.azure.com domain
  const isAdxLink = queryOrLink.startsWith("http") && queryOrLink.includes("kusto.azure.com");

  // Generate the final ADX link: use input as-is if it's already a link,
  // otherwise generate a new link from the Kusto query
  const adxLink = isAdxLink ? queryOrLink : await generateADXLink(queryOrLink);

  // Check if we successfully have an ADX link to open
  if (adxLink) {
    try {
      // Open the ADX link in a new browser tab/window
      // "_blank" opens in new tab, "noopener,noreferrer" provides security by preventing
      // the new page from accessing the parent window and removes referrer information
      window.open(adxLink, "_blank", "noopener,noreferrer");
    } catch (error) {
      // Handle errors that might occur when trying to open the window
      // This could happen due to popup blockers or browser security settings
      const errorMessage = "Failed to open Azure Data Explorer. Please check your browser settings.";
      // Log the error to console for debugging purposes
      console.error(errorMessage, error);
      // Use provided error callback if available, otherwise fallback to browser alert
      if (onError) {
        onError(errorMessage);
      } else {
        alert(errorMessage);
      }
    }
  } else {
    // Handle case where we couldn't generate or validate the ADX link
    // Provide different error messages based on whether input was a link or query
    const errorMessage = isAdxLink
      ? "Invalid ADX link provided." // Input looked like a link but was invalid
      : "Unable to generate ADX link. Please ensure the query is valid."; // Query processing failed
    // Use provided error callback if available, otherwise fallback to browser alert
    if (onError) {
      onError(errorMessage);
    } else {
      alert(errorMessage);
    }
  }
};

/**
 * Validates if a string appears to be a valid Kusto query
 * @param query - The query string to validate
 * @returns true if the query appears valid, false otherwise
 */
export const isValidKustoQuery = (query: string): boolean => {
  // Check if query parameter exists and is not just whitespace
  if (!query?.trim()) return false;

  // Remove leading/trailing whitespace for consistent processing
  const trimmedQuery = query.trim();

  // Define array of common Kusto Query Language (KQL) keywords and operators
  // These are the most frequently used commands in Kusto queries
  const kustoKeywords = [
    "let", // Variable declaration: let variable = value
    "where", // Filter rows: TableName | where ColumnName == "value"
    "project", // Select specific columns: TableName | project Column1, Column2
    "extend", // Add computed columns: TableName | extend NewColumn = Column1 + Column2
    "summarize", // Aggregate data: TableName | summarize count() by ColumnName
    "order", // Sort results: TableName | order by ColumnName
    "top", // Get top N results: TableName | top 10 by ColumnName
    "take", // Limit results: TableName | take 100
    "limit", // Alternative to take: TableName | limit 100
    "join", // Join tables: Table1 | join Table2 on CommonColumn
    "union", // Combine tables: union Table1, Table2
    "distinct", // Get unique values: TableName | distinct ColumnName
    "count", // Count rows: TableName | count
    "evaluate", // Call functions: TableName | evaluate bag_unpack(JsonColumn)
    "render", // Visualize data: TableName | render barchart
    "print" // Display literal values: print "Hello World"
  ];

  // Validation logic: check if the query contains at least one Kusto keyword
  // OR matches the pattern of a simple table reference with optional pipe operations
  return (
    // Check if any Kusto keyword exists in the query (case-insensitive comparison)
    kustoKeywords.some((keyword) => trimmedQuery.toLowerCase().includes(keyword.toLowerCase())) ||
    // Alternative validation: check if query matches table reference pattern
    // Pattern: word characters (table name) optionally followed by whitespace and pipe operations
    // Example: "MyTable" or "MyTable | where Column1 == 'value'"
    /^\w+(\s*\|.*)?$/i.test(trimmedQuery)
  );
};

// Export all functions as a default object for convenient importing
// This allows importing the entire module: import adxUtils from './adx-utils'
// Or importing specific functions: import { generateADXLink } from './adx-utils'
export default {
  generateADXLink, // Function to create ADX links from Kusto queries
  openQueryInADX, // Function to open queries/links in Azure Data Explorer
  isValidKustoQuery // Function to validate Kusto query syntax
};
